import React, { Component } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import classNames from 'classnames';

import { isEqual } from 'lodash';
import { PopoverPosition } from '@blueprintjs/core';
import { Measurement } from '@kentik/ui-shared/query/query.types';
import { AppProtocolsDictionary } from '@kentik/ui-shared/dictionary/types';
import { Box, DropdownMenu, Menu, MenuDivider, MenuItem, Text } from 'core/components';
import { InputGroup } from 'core/form';
import { filterTreeNodes, mergeExpandedState, transformMeasurementsToBlueprintTree } from './metricsTreeData';
import { UdeTreeNode, updateNode } from './treeUtils';
import DirectionalButtons from './components/DirectionalButtons';

const PopoverContent = styled.div`
  width: 425px;
  max-height: calc(100vh - 64px);
  padding: 8px;
  display: flex;
  flex-direction: column;

  .bp4-tree-node-secondary-label {
    font-size: 11px;
    color: #5c7080;
    padding-right: 8px;
  }
`;

const SearchWrapper = styled(Box)`
  flex-shrink: 0;

  .bp4-input {
    font-size: 13px;
  }

  .bp4-icon {
    top: 1px;
    left: 2px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

interface MetricDimensionSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  measurement: string | null;
  type: 'metric' | 'dimension';
  selectedItems: Array<{ name: string }>;
  onSelect: (itemName: string) => void;
  onRemove: (itemName: string) => void;
  measurements: Measurement[];
  measurementLookback?: number;
  /** When true, already-selected items are hidden from the list instead of highlighted */
  hideSelected?: boolean;
  /** When true, the dropdown stays open after selecting an item */
  keepOpenOnSelect?: boolean;
  selectedDimensions?: Array<{ name: string }>;
  appProtocols?: AppProtocolsDictionary;
  children?: React.ReactNode | ((props: { isOpen: boolean }) => React.ReactNode);
}

interface MetricDimensionSelectorState {
  treeContents: UdeTreeNode[];
  search: string;
  preSearchTreeState: UdeTreeNode[] | null;
}

// Cache for the base tree to avoid recomputing on every search
interface TreeCache {
  measurement: string | null;
  type: 'metric' | 'dimension';
  baseTree: UdeTreeNode[];
}

interface SelectorMenuProps {
  groupedNodes: Map<string, UdeTreeNode[]>;
  selectedItems: Array<{ name: string }>;
  disabledItems: Set<string>;
  keepOpenOnSelect?: boolean;
  onNodeClick: (node: UdeTreeNode) => void;
  onSelect: (value: string) => void;
}

function SelectorMenu(props: SelectorMenuProps) {
  const { groupedNodes, selectedItems, disabledItems, keepOpenOnSelect, onNodeClick, onSelect } = props;

  const groupKeys = Array.from(groupedNodes.keys()).sort((a, b) => {
    // Uncategorized first
    if (a === 'Uncategorized') {
      return -1;
    }
    if (b === 'Uncategorized') {
      return 1;
    }
    return a.localeCompare(b);
  });

  return (
    <>
      {groupKeys.map((category) => {
        const items = groupedNodes.get(category) || [];

        if (items.length === 0) {
          return null;
        }

        const isUncategorized = category === 'Uncategorized';
        const isSubMenu =
          !isUncategorized &&
          items[0].nodeData?.Subcategory !== undefined &&
          items[0].nodeData.Subcategory !== category;

        if (isSubMenu) {
          const subgroups = new Map<string, UdeTreeNode[]>();
          items.forEach((node) => {
            const subcategory = node.nodeData?.Subcategory || 'Uncategorized';
            if (!subgroups.has(subcategory)) {
              subgroups.set(subcategory, []);
            }
            subgroups.get(subcategory)?.push(node);
          });

          return (
            <React.Fragment key={category}>
              <MenuDivider />
              <MenuItem text={<Text small>{category}</Text>} icon="numerical">
                <SelectorMenu
                  groupedNodes={subgroups}
                  selectedItems={selectedItems}
                  disabledItems={disabledItems}
                  keepOpenOnSelect={keepOpenOnSelect}
                  onNodeClick={onNodeClick}
                  onSelect={onSelect}
                />
              </MenuItem>
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={category}>
            {!isUncategorized && <MenuDivider title={category} />}
            {items.map((node) => {
              const nodeKey = String(node.id);
              return (
                <MenuItem
                  key={nodeKey}
                  text={
                    node.isDirectionalNode && node.nodeData?.Directions ? (
                      <DirectionalButtons
                        label={node.nodeData.Label || node.value || nodeKey}
                        directions={node.nodeData.Directions}
                        onSelect={onSelect}
                        matches={selectedItems}
                        highlightSelected
                      />
                    ) : (
                      <Text textAlign="left" small flex={1}>
                        {node.nodeData?.Label || node.value || nodeKey}
                      </Text>
                    )
                  }
                  icon={node.icon || 'blank'}
                  className={node.className}
                  selected={Boolean(node.nodeData?.selected)}
                  disabled={Boolean(node.disabled) || disabledItems.has(node.value || nodeKey)}
                  shouldDismissPopover={!keepOpenOnSelect}
                  onClick={() => onNodeClick(node)}
                />
              );
            })}
          </React.Fragment>
        );
      })}
    </>
  );
}

class MetricDimensionSelector extends Component<MetricDimensionSelectorProps, MetricDimensionSelectorState> {
  state = {
    treeContents: [] as UdeTreeNode[],
    search: '',
    preSearchTreeState: null
  };

  // Cache the base tree (before search/selection) to avoid recomputing on every search
  private treeCache: TreeCache | null = null;

  componentDidMount() {
    this.initializeTree();
  }

  componentDidUpdate(prevProps: MetricDimensionSelectorProps) {
    const { measurement, type, selectedItems, measurementLookback, isOpen } = this.props;
    const measurementChanged = prevProps.measurement !== measurement;
    const typeChanged = prevProps.type !== type;
    const selectedItemsChanged = !isEqual(prevProps.selectedItems, selectedItems);
    const lookbackChanged = prevProps.measurementLookback !== measurementLookback;

    // Reset search when popover closes
    if (prevProps.isOpen && !isOpen) {
      this.setState({ search: '', preSearchTreeState: null }, () => {
        this.initializeTree();
      });
    }

    if (measurementChanged || typeChanged || lookbackChanged) {
      // Invalidate cache when measurement or type changes
      this.treeCache = null;
      this.initializeTree();
    } else if (selectedItemsChanged) {
      // Only re-mark selection, no need to rebuild the full tree
      this.updateTreeSelection();

      // Trigger Popper.js reposition — layout changes from adding/removing items
      // shift the target button, but Popper only listens for scroll/resize events
      // if (isOpen) {
      //   requestAnimationFrame(() => {
      //     window.dispatchEvent(new Event('resize'));
      //   });
      // }
    }
  }

  /**
   * Builds or retrieves the cached base tree (filtered by measurement and type, before search/selection)
   */
  private getBaseTree = (): UdeTreeNode[] => {
    const { measurements, measurement, type } = this.props;

    if (!measurement) {
      return [];
    }

    // Return cached tree if available and valid
    if (this.treeCache && this.treeCache.measurement === measurement && this.treeCache.type === type) {
      return this.treeCache.baseTree;
    }

    const tree = transformMeasurementsToBlueprintTree(measurements);
    const baseTree = this.filterToMeasurement(tree, measurement, type);

    // Cache it
    this.treeCache = { measurement, type, baseTree };
    return baseTree;
  };

  /**
   * Applies search filter and selection marking to the base tree
   */
  private buildFinalTree = (baseTree: UdeTreeNode[], searchValue: string): UdeTreeNode[] => {
    const { selectedItems = [] } = this.props;

    let filteredTree = baseTree;

    // Apply search filter if there's a search query
    if (searchValue) {
      filteredTree = mergeExpandedState(filteredTree, filterTreeNodes(filteredTree, searchValue.toLowerCase().trim()));
    }

    // Mark selected items
    return this.markSelectedNodes(filteredTree, selectedItems);
  };

  initializeTree = () => {
    const { measurement } = this.props;
    const { search } = this.state;

    if (measurement) {
      const baseTree = this.getBaseTree();
      const treeWithSelection = this.buildFinalTree(baseTree, search);
      this.setState({ treeContents: treeWithSelection });
    }
  };

  /**
   * Updates only the selection state without rebuilding the tree
   */
  private updateTreeSelection = () => {
    const { search } = this.state;
    const baseTree = this.getBaseTree();
    const treeWithSelection = this.buildFinalTree(baseTree, search);
    this.setState({ treeContents: treeWithSelection });
  };

  handleSearchChange = (value: string) => {
    const { measurement } = this.props;
    const { treeContents, preSearchTreeState, search: currentSearch } = this.state;

    // If starting to search, save the current tree state
    if (!currentSearch && value && !preSearchTreeState) {
      this.setState({ preSearchTreeState: treeContents });
    }

    // If clearing search, restore the pre-search state
    if (currentSearch && !value && preSearchTreeState) {
      this.setState({
        search: value,
        treeContents: preSearchTreeState,
        preSearchTreeState: null
      });
      return;
    }

    this.setState({ search: value }, () => {
      if (measurement) {
        const baseTree = this.getBaseTree();
        const treeWithSelection = this.buildFinalTree(baseTree, value);
        this.setState({ treeContents: treeWithSelection });
      }
    });
  };

  filterToMeasurement = (nodes: UdeTreeNode[], measurementPath: string, type: string): UdeTreeNode[] => {
    // Find the node that matches the measurement path
    const findMeasurementNode = (nodeList: UdeTreeNode[], path: string): UdeTreeNode | null => {
      for (const node of nodeList) {
        if (node.id === path) {
          return node;
        }
        if (node.childNodes && node.childNodes.length > 0) {
          const found = findMeasurementNode(node.childNodes, path);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };

    const measurementNode = findMeasurementNode(nodes, measurementPath);
    if (!measurementNode) {
      return [];
    }

    // Filter to show only dimensions or metrics under this measurement
    const filterByType = (node: UdeTreeNode): UdeTreeNode | null => {
      if (node.childNodes && node.childNodes.length > 0) {
        const filteredChildren = node.childNodes
          .filter(
            (child) =>
              !child.nodeData?.FilterOnly &&
              ((child.childNodes &&
                child.childNodes.length > 0 &&
                // Make sure this child node is under the correct measurement path, avoid nested measurements
                typeof child.id === 'string' &&
                child.id?.startsWith?.(`${measurementPath}/${type}`)) ||
                child.type === type)
          )
          .map((child) => filterByType(child))
          .filter((n): n is UdeTreeNode => Boolean(n));

        if (filteredChildren.length > 0) {
          return { ...node, childNodes: filteredChildren, isExpanded: true };
        }
        return null;
      }
      return node.type === type && !node.nodeData?.FilterOnly ? node : null;
    };

    return [filterByType(measurementNode)].filter((n): n is UdeTreeNode => Boolean(n));
  };

  markSelectedNodes = (nodes: UdeTreeNode[], selectedItems: { name: string }[]) => {
    const markNode = (node: UdeTreeNode) => {
      let isSelected = selectedItems.some((si) => si.name === node.value);

      if (node.isDirectionalNode) {
        const keys = Object.values(node.nodeData?.Directions || {}).map((d) => d.key);
        isSelected = keys.some((key) => selectedItems.some((si) => si.name === key));
      }

      const updatedNode = {
        ...node,
        className: classNames(node.type, { selected: isSelected }),
        type: node.type || 'metric',
        icon: node.icon,
        nodeData: { ...node.nodeData, selected: isSelected }
      };

      if (node.childNodes && node.childNodes.length > 0) {
        updatedNode.childNodes = node.childNodes.map(markNode);
      }

      return updatedNode;
    };

    return nodes.map(markNode);
  };

  handleSelect = (value: string) => {
    const { hideSelected, keepOpenOnSelect, onClose, onRemove, onSelect, selectedItems } = this.props;
    if (hideSelected) {
      // When hiding selected items, click always adds
      onSelect(value);
    } else {
      // Toggle behavior: click selected item to remove, unselected to add
      const isSelected = selectedItems.some((si) => si.name === value);
      if (isSelected) {
        onRemove(value);
      } else {
        onSelect(value);
      }
    }
    // Close after selection unless keepOpenOnSelect is set
    if (!keepOpenOnSelect) {
      onClose();
    }
  };

  handleNodeClick = (node: UdeTreeNode) => {
    const { type, selectedItems = [] } = this.props;
    const { treeContents } = this.state;

    if (!node.childNodes || node.childNodes.length === 0) {
      if (node.isDirectionalNode) {
        const keys = Object.values(node.nodeData?.Directions || {}).map((d) => d.key);
        const allSelected = keys.every((key) => selectedItems.some((si) => si.name === key));

        if (allSelected) {
          keys.forEach((key) => this.handleSelect(key));
        } else {
          keys.forEach((key) => {
            if (!selectedItems.some((si) => si.name === key)) {
              this.handleSelect(key);
            }
          });
        }
      } else if (node.type === type && node.value) {
        this.handleSelect(node.value);
      }
    } else {
      // Toggle expand/collapse
      const updatedTree = treeContents.map((n) =>
        updateNode(n, node, (childNode) => ({
          ...childNode,
          isExpanded: !childNode.isExpanded
        }))
      );
      this.setState({
        treeContents: this.markSelectedNodes(updatedTree, selectedItems)
      });
    }
  };

  /**
   * Determines which app protocol metrics should be hidden based on selected dimensions.
   */
  private getDisabledAppProtocolMetrics = (): Set<string> => {
    const { selectedDimensions = [], type, appProtocols } = this.props;

    if (type !== 'metric' || selectedDimensions.length === 0) {
      return new Set();
    }

    // Find the first selected app protocol dimension
    const appProtocolDim = selectedDimensions.find((d) => d.name.startsWith('ktappprotocol__'));
    if (!appProtocolDim) {
      return new Set();
    }

    const protocolName = appProtocolDim.name.split('__')[1];
    if (!appProtocols) {
      return new Set();
    }

    const selectedProtocol = appProtocols[protocolName];
    if (!selectedProtocol) {
      return new Set();
    }

    // Collect display names of all other protocols to hide their metrics
    const disabledProtocols = new Set<string>();
    Object.values(appProtocols).forEach((protocol) => {
      if (protocol.id !== selectedProtocol.id) {
        disabledProtocols.add(String(protocol.display_name));
      }
    });

    return disabledProtocols;
  };

  private isAppProtocolMetricDisabled = (node: UdeTreeNode, disabledProtocols: Set<string>): boolean => {
    if (disabledProtocols.size === 0) {
      return false;
    }

    const metricKey = node.value;
    if (!metricKey || !metricKey.startsWith('ktappprotocol__')) {
      return false;
    }

    const category = (node.nodeData?.Category as string | undefined) ?? '';
    return disabledProtocols.has(category);
  };

  getGroupedNodes = (nodes: UdeTreeNode[]): Map<string, UdeTreeNode[]> => {
    const { selectedItems = [], hideSelected } = this.props;
    const disabledProtocols = this.getDisabledAppProtocolMetrics();
    const groups = new Map<string, UdeTreeNode[]>();

    const collect = (nodeList: UdeTreeNode[]) => {
      nodeList.forEach((node) => {
        if (node.childNodes && node.childNodes.length > 0) {
          collect(node.childNodes);
        } else {
          // Hide app protocol metrics from other protocols entirely
          if (node.type === 'metric' && this.isAppProtocolMetricDisabled(node, disabledProtocols)) {
            return;
          }

          // When hideSelected is enabled, filter out already-selected items
          if (hideSelected && selectedItems.some((si) => si.name === node.value)) {
            return;
          }

          const category = (node.nodeData?.Category as string | undefined) || 'Uncategorized';
          if (!groups.has(category)) {
            groups.set(category, []);
          }
          const group = groups.get(category);
          if (group) {
            group.push(node);
          }
        }
      });
    };

    collect(nodes);
    return groups;
  };

  getDisabledItems() {
    const { selectedDimensions = [], selectedItems = [], type } = this.props;
    if (type === 'dimension') {
      return new Set(selectedItems.map((m) => m.name));
    }
    return new Set(selectedDimensions.map((d) => d.name));
  }

  render() {
    const { isOpen, onClose, type, children, keepOpenOnSelect, selectedItems } = this.props;

    const { treeContents, search } = this.state;
    // Do not show tree root node, just its children as we've already filtered to the measurement of interest
    const treeChildContents = treeContents?.[0]?.childNodes || [];

    const groupedNodes = this.getGroupedNodes(treeChildContents);
    const disabledItems = this.getDisabledItems();

    // Use DropdownMenu for both dimensions and metrics
    return (
      <DropdownMenu
        isOpen={isOpen}
        onInteraction={(nextOpenState, e) => {
          if (nextOpenState) {
            return;
          }
          // For keepOpenOnSelect, only close on Escape key or click outside the popover content;
          // suppress close events originating from inside the menu (e.g. icon/label clicks).
          if (keepOpenOnSelect) {
            const target = (e?.target as HTMLElement | null) ?? null;
            const isInsideContent = !!target?.closest('.ude-metric-dimension-selector-popover');
            const isEscape = (e as unknown as KeyboardEvent | undefined)?.key === 'Escape';
            if (isInsideContent && !isEscape) {
              return;
            }
          }
          onClose();
        }}
        position={PopoverPosition.RIGHT_TOP}
        boundary="viewport"
        popoverClassName="ude-metric-dimension-selector-popover"
        modifiers={{
          flip: { enabled: true },
          hide: { enabled: false },
          preventOverflow: { enabled: true, padding: 20 }
        }}
        canEscapeKeyClose
        enforceFocus={false}
        content={
          <PopoverContent>
            <SearchWrapper>
              <InputGroup
                type="search"
                fill
                autoFocus
                leftIcon="search"
                placeholder={`Filter ${type === 'metric' ? 'units' : 'dimensions'}...`}
                value={search}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.handleSearchChange(e.target.value)}
              />
            </SearchWrapper>

            <Box flex={1} borderTop="thin" overflow="auto" m="8px -8px -8px" p="4px">
              {groupedNodes.size === 0 ? (
                <Text small muted p="12px" textAlign="center" display="block">
                  No {type === 'metric' ? 'units' : 'dimensions'} were found
                </Text>
              ) : (
                <Menu p={0}>
                  <SelectorMenu
                    groupedNodes={groupedNodes}
                    selectedItems={selectedItems}
                    disabledItems={disabledItems}
                    keepOpenOnSelect={keepOpenOnSelect}
                    onNodeClick={this.handleNodeClick}
                    onSelect={this.handleSelect}
                  />
                </Menu>
              )}
            </Box>
          </PopoverContent>
        }
      >
        {typeof children === 'function' ? children({ isOpen }) : children}
      </DropdownMenu>
    );
  }
}

export default observer(MetricDimensionSelector);
